import AppLoadingShell from "@/components/AppLoadingShell";
import React from "react";
import useVesselTransition from "@/hooks/useVesselTransition";
import useBoutiqueWallpaper from "@/hooks/useBoutiqueWallpaper";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PageContextProvider } from "@/context/PageContext";
import { CompanionAssetsProvider } from "@/companion/CompanionAssetsContext";
import { CompanionProvider } from "@/companion/CompanionContext";
import { DeploymentContentProvider } from "@/companion/DeploymentContentContext";
import TodayLayout from '@/components/toto/TodayLayout';
import ProtectedLayout from "@/components/ProtectedLayout";
import "@/styles/toto-spatial-refinement.css";
import "@/styles/toto-spatial-system.css";
import "@/styles/toto-static-foundation.css";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Home from "@/pages/Home";
import TotoCalendar from "@/pages/TotoCalendar";
import TotoWorkspaces from "@/pages/TotoWorkspaces";
import ZenotiWorkspace from "@/pages/ZenotiWorkspace";
import TeamStewardship from "@/pages/TeamStewardship";
import KPITrackers from "@/pages/KPITrackers";
import PerformanceRecognition from "@/pages/PerformanceRecognition";
import Pulse from "@/pages/Pulse";
import People from "@/pages/People";
import CompanionChat from "@/pages/TotoChat";
import SonmiEmployees from "@/pages/SonmiEmployees";
import SonmiSignonBonus from "@/pages/SonmiSignonBonus";
import BudgetWorkspace from "@/pages/BudgetWorkspace";
import HR from "@/pages/HR";
import DocumentsPage from "@/pages/DocumentsPage";
import WorkflowDocumentView from "@/components/documents/WorkflowDocumentView";
import DocumentDetail from "@/pages/DocumentDetail";
import EODWorkflow from "@/pages/EODWorkflow";
import JourneyPlanning from "@/pages/JourneyPlanning";
import InventoryDashboard from "@/pages/inventory/InventoryDashboard";
import RetailAudit from "@/pages/inventory/RetailAudit";
import VarianceReview from "@/pages/inventory/VarianceReview";
import VariancesLanding from "@/pages/inventory/VariancesLanding";
import ReceivingRoute from "@/pages/inventory/Receiving";
import Linen from "@/pages/inventory/Linen";
import Scheduling from "@/pages/Scheduling";
import ScheduleMT from "@/pages/ScheduleMT";
import ScheduleEG from "@/pages/ScheduleEG";
import ScheduleEGPreview from "@/pages/ScheduleEGPreview";
import TimeOff from "@/pages/TimeOff";
import SchedulingPreferences from "@/pages/SchedulingPreferences";
import ZenotiQueue from "@/pages/ZenotiQueue";
import MySchedule from "@/pages/MySchedule";
import Coverage from "@/pages/Coverage";
import Swaps from "@/pages/Swaps";
import FloorPlanSettings from "@/pages/FloorPlanSettings";
import JourneyPathDetail from "@/pages/JourneyPathDetail";
import JourneyGuide from "@/pages/JourneyGuide";
import LiquidGlassPrototype from "@/pages/LiquidGlassPrototype";
import PrototypeIndex from "@/pages/prototypes/PrototypeIndex";
import WelcomeKatrina from "@/pages/prototypes/WelcomeKatrina";
import KatrinaHome from "@/pages/prototypes/KatrinaHome";
import MessageOpened from "@/pages/prototypes/MessageOpened";
import Handoff from "@/pages/prototypes/Handoff";
import SonmiIntroduction from "@/pages/prototypes/SonmiIntroduction";
import PayrollUnderstanding from "@/pages/prototypes/PayrollUnderstanding";
import CooHome from "@/pages/prototypes/CooHome";
import PresencePayrollReveal from "@/pages/prototypes/PresencePayrollReveal";
import PayrollEnginePreview from "@/pages/PayrollEnginePreview";
const NavigationPreview = React.lazy(() => import("@/pages/NavigationPreview"));
const PrintableAssets = React.lazy(() => import("@/pages/PrintableAssets"));
const EmailBuilder = React.lazy(() => import("@/pages/EmailBuilder"));
const PayrollHandoffPage = React.lazy(() => import("@/pages/PayrollHandoffPage"));
const PayrollPreparationWorkspace = React.lazy(() => import("@/pages/PayrollPreparationWorkspace"));
const GoogleBusinessAuthorization = React.lazy(() => import("@/pages/GoogleBusinessAuthorization"));
const GoogleReviewCalculator = React.lazy(() => import("@/pages/GoogleReviewCalculator"));
const ReportCalculatorWorkspace = React.lazy(() => import("@/pages/ReportCalculatorWorkspace"));
const PayrollReadiness = React.lazy(() => import("@/pages/PayrollReadiness"));
const EGCalculatorReview = React.lazy(() => import("@/pages/EGCalculatorReview"));
const ZenotiWorkforceRoster = React.lazy(() => import("@/pages/ZenotiWorkforceRoster"));
const MTMembershipCalculator = React.lazy(() => import("@/pages/MTMembershipCalculator"));
const TippyCalculator = React.lazy(() => import("@/pages/TippyCalculator"));
import CompanionAssets from "@/pages/TotoAssets";
import Daily from "@/pages/Daily";
import PublicOffer from "@/pages/PublicOffer";
import PublicWitness from "@/pages/PublicWitness";
import PublicApply from "@/pages/PublicApply";
import Tour from "@/pages/Tour";
import PresenceLab from "@/pages/labs/PresenceLab";
import TotoReferenceSurface from "@/pages/TotoReferenceSurface";
import YextImport from "@/pages/reviews/YextImport";
import GoogleReviewEvidence from "@/pages/GoogleReviewEvidence";
import TotoIntroduction from "@/pages/TotoIntroduction";
import MyDay from "@/pages/MyDay";
import TodaysGoals from "@/pages/TodaysGoals";
import BackgroundSettings from "@/pages/BackgroundSettings";
import ProgramAudit from "@/pages/ProgramAudit";
import TotoProfile from "@/pages/TotoProfile";
import HiringTab from "@/pages/HiringTab";
// SONMI visual studies (Feb 2026) — hidden, static, unauthenticated,
// no sidebar, no backend. Removable without affecting the preserved product.
import SonmiStudyShell from "@/pages/sonmi_study/Shell";
import SonmiStudyPeopleVessel from "@/pages/sonmi_study/PeopleVessel";
import SonmiStudyExpanded from "@/pages/sonmi_study/Expanded";
import SonmiStudyVessel from "@/pages/sonmi_study/Vessel";
import SonmiStudyVesselCorrection from "@/pages/sonmi_study/VesselCorrection";
import SonmiStudyVesselCoAuthored from "@/pages/sonmi_study/VesselCoAuthored";
// First real SONMI instrument — the First Arrival Observatory.
import FirstArrival from "@/pages/first_arrival/FirstArrival";
// SONMI Atrium — the new front door.
import Atrium from "@/pages/atrium/Atrium";
import EcologyView from "@/pages/atrium/EcologyView";
import StudiesIndex from "@/pages/atrium/StudiesIndex";
// Frame 001 Laboratory — the visual research environment.
// Not linked from the Atrium. Accessible by URL only.
import Frame001Lab from "@/pages/lab/Frame001Lab";
// Laboratory 2 · Perspective — perspective-as-interface research.
import PerspectiveLab from "@/pages/lab/perspective/PerspectiveLab";
// Frame 002 · Production route.
import Frame002 from "@/pages/lab/frame002/Frame002";
// Convergence · isolated prototype.
import ConvergenceLab from "@/pages/lab/convergence/Convergence";
import BeaconsLab from "@/pages/lab/beacons/BeaconsLab";
// Presentation shell — the actual presentation flow.
import Presentation from "@/pages/presentation/Presentation";
// Workspace — SONMI's first shared-presence surface. See sonmi_workspace.py.
import WorkspacePage from "@/pages/workspace/WorkspacePage";
// Workspace DevTools — SONMI's cognitive inspector. Research surface, not product.
import WorkspaceDebugPage from "@/pages/workspace/WorkspaceDebugPage";
// Worlds — SONMI's domain understanding. Research surface.
import WorldsPage from "@/pages/workspace/WorldsPage";
// Vessels — SONMI's Vessel envelope (Phase 2). See /app/CANON/VESSEL.md.
import VesselsPage from "@/pages/workspace/VesselsPage";
// Vault — governed Document Library (Phase 1 of Track A).
// Separate from Workspace and Vessels: nothing here enters the LLM
// interpretation pipeline. See /app/memory/PERSON_VESSEL_BRIEF.md.
import VaultLibraryPage from "@/pages/vault/VaultLibraryPage";
import { Toaster } from "sonner";
import "@/styles/toto-page-shell-contract.css";
import "@/styles/sonmi-liquid-shell.css";
import "@/styles/sonmi-refraction.css";
import "@/styles/toto-wallpaper.css";
import "@/components/payroll/PayrollSurface.css";

function PilotEntry() {
  const { user, loading } = useAuth();

  if (loading) {
    return <AppLoadingShell />;
  }

  return <Navigate to={user ? "/home" : "/login"} replace />;
}

function Router() {
  const location = useLocation();
  const { user, loading } = useAuth();
  const navigationType = useNavigationType();
  const displayedLocation = useVesselTransition(location, Boolean(user) && !loading, navigationType);
  useBoutiqueWallpaper(user);
  const researchEnabled = process.env.NODE_ENV !== "production" || process.env.REACT_APP_ENABLE_RESEARCH_ROUTES === "true";
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const researchPath = ["/lab/", "/prototypes", "/sonmi/study/", "/present", "/tour"].some(
    (prefix) => location.pathname.startsWith(prefix)
  );
  if (!researchEnabled && researchPath) {
    return <Navigate to="/login" replace />;
  }
  return (
    <Routes location={displayedLocation}>
      <Route path="/login" element={<Login />} />
      <Route path="/google-business/authorization" element={<React.Suspense fallback={<AppLoadingShell />}><GoogleBusinessAuthorization /></React.Suspense>} />
      <Route path="/offer/:token" element={<PublicOffer />} />
      <Route path="/witness/:token" element={<PublicWitness />} />
      <Route path="/sign/:token" element={<PublicWitness />} />
      <Route path="/apply" element={<PublicApply />} />
      <Route path="/tour" element={<Tour />} />
      {/* Pilot entry is deliberately quiet and role-safe. The Atrium is a
          deliberately unlinked origin room, reachable from Home's SONMI mark. */}
      <Route path="/" element={<PilotEntry />} />
      <Route element={<ProtectedLayout navigationKey="today"><TodayLayout/></ProtectedLayout>}>
       <Route path="/home" element={<Home/>}/>
       <Route path="/my-day" element={<MyDay/>}/>
       <Route path="/today/goals" element={<TodaysGoals/>}/>
       <Route path="/today/team" element={<People todaySchedule/>}/>
       <Route path="/eod" element={<EODWorkflow/>}/>
       <Route path="/operations/eod" element={<EODWorkflow/>}/>
      </Route>

      <Route path="/workspaces/printable-assets" element={<ProtectedLayout><React.Suspense fallback={<AppLoadingShell />}><PrintableAssets /></React.Suspense></ProtectedLayout>} />
      <Route path="/workspaces" element={<ProtectedLayout><TotoWorkspaces /></ProtectedLayout>} />
      <Route path="/workspaces/report-calculator" element={<ProtectedLayout><React.Suspense fallback={<AppLoadingShell />}><ReportCalculatorWorkspace /></React.Suspense></ProtectedLayout>} />
      <Route path="/workspaces/zenoti" element={<ProtectedLayout><ZenotiWorkspace /></ProtectedLayout>} />
      <Route path="/workspaces/performance/trackers" element={<ProtectedLayout><KPITrackers /></ProtectedLayout>} />
      <Route path="/workspaces/performance-recognition" element={<ProtectedLayout><PerformanceRecognition /></ProtectedLayout>} />
      <Route path="/workspaces/team-stewardship" element={<ProtectedLayout><TeamStewardship /></ProtectedLayout>} />
      <Route path="/workspaces/hiring" element={<ProtectedLayout><HiringTab /></ProtectedLayout>} />
      <Route path="/sonmi/atrium" element={<ProtectedLayout><Atrium /></ProtectedLayout>} />
      <Route path="/sonmi/ecology" element={<EcologyView />} />
      <Route path="/sonmi/studies" element={<StudiesIndex />} />
      {/* Frame 001 Laboratory — research route, URL-only. */}
      <Route path="/lab/frame-001" element={<Frame001Lab />} />
      {/* Laboratory 2 · Perspective — research route, URL-only. */}
      <Route path="/lab/perspective" element={<PerspectiveLab />} />
      {/* Frame 002 · Production — locked route for the current presentation. */}
      <Route path="/lab/frame-002" element={<Frame002 />} />
      {/* Convergence · isolated prototype — Frame 002 acts + a fifth phase. */}
      <Route path="/lab/convergence" element={<ConvergenceLab />} />
      {/* Experimental — convergence without movement. See BEACONS_BRIEF.md. */}
      <Route path="/lab/beacons" element={<BeaconsLab />} />
      {/* TOTO transition behavior — isolated Design Lab reference surface. */}
      <Route path="/lab/toto-transitions" element={researchEnabled ? <TotoReferenceSurface /> : <Navigate to="/login" replace />} />
      {/* Presentation shell — Frame 001 → Frame 002 → Convergence → SONMI. */}
      <Route path="/present" element={<Presentation />} />
      {/* SONMI Workspace — shared-presence prototype (Path A). */}
      <Route path="/workspace" element={<ProtectedLayout><WorkspacePage /></ProtectedLayout>} />
      {/* SONMI Workspace DevTools — cognitive inspector, research surface only. */}
      <Route path="/workspace/debug" element={<ProtectedLayout><WorkspaceDebugPage /></ProtectedLayout>} />
      {/* SONMI Vessels — the Vessel envelope. See /app/CANON/VESSEL.md. */}
      <Route path="/workspace/vessels" element={<ProtectedLayout><VesselsPage /></ProtectedLayout>} />
      {/* SONMI Worlds — legacy path. A World is now contained by a Vessel;
          this URL redirects to /workspace/vessels so old links keep working. */}
      <Route path="/workspace/worlds" element={<Navigate to="/workspace/vessels" replace />} />
      {/* Vault · Document Library — deliberately not under /workspace.
          Nothing in the vault enters the interpretation pipeline. See
          /app/backend/vault.py and /app/memory/PERSON_VESSEL_BRIEF.md. */}
      <Route path="/vault" element={<Navigate to="/vault/library" replace />} />
      <Route path="/vault/library" element={<ProtectedLayout><VaultLibraryPage /></ProtectedLayout>} />
      {/* Retired payroll era: preserve source as a study, but never re-enter it from the live product. */}
      <Route path="/payroll/understanding" element={<Navigate to="/home" replace />} />
      <Route path="/pulse" element={<ProtectedLayout><Pulse /></ProtectedLayout>} />
      <Route path="/people" element={<ProtectedLayout><People key="team-directory" /></ProtectedLayout>} />

      <Route path="/payroll" element={<Navigate to="/home" replace />} />
      <Route path="/payroll/handoff" element={<ProtectedLayout><React.Suspense fallback={null}><PayrollHandoffPage /></React.Suspense></ProtectedLayout>} />
      <Route path="/payroll/prepare" element={<ProtectedLayout><React.Suspense fallback={null}><PayrollPreparationWorkspace /></React.Suspense></ProtectedLayout>} />
      <Route path="/payroll/readiness" element={<ProtectedLayout><React.Suspense fallback={null}><PayrollReadiness /></React.Suspense></ProtectedLayout>} />
      <Route path="/payroll/calculators/google-reviews" element={<ProtectedLayout><React.Suspense fallback={<AppLoadingShell />}><GoogleReviewCalculator /></React.Suspense></ProtectedLayout>} />
      <Route path="/payroll/calculators/eg-enhancement" element={<ProtectedLayout><React.Suspense fallback={null}><EGCalculatorReview /></React.Suspense></ProtectedLayout>} />
      <Route path="/payroll/calculators/mt-membership" element={<ProtectedLayout><React.Suspense fallback={null}><MTMembershipCalculator /></React.Suspense></ProtectedLayout>} />
      <Route path="/payroll/calculators/tippy-gratuities" element={<ProtectedLayout><React.Suspense fallback={null}><TippyCalculator /></React.Suspense></ProtectedLayout>} />
      <Route path="/payroll/reports/zenoti-workforce-roster" element={<ProtectedLayout><React.Suspense fallback={null}><ZenotiWorkforceRoster /></React.Suspense></ProtectedLayout>} />
      <Route path="/admin/payroll-preparation" element={<Navigate to="/home" replace />} />
      <Route path="/admin/payroll-rules" element={<Navigate to="/home" replace />} />
      {/* SONMI · hidden admin surfaces (see /app/memory/SONMI_INTERACTION_PRINCIPLE.md). */}
      <Route path="/admin/employees" element={<ProtectedLayout><SonmiEmployees /></ProtectedLayout>} />
      <Route path="/admin/sonmi/sign-on-bonus" element={<ProtectedLayout><SonmiSignonBonus /></ProtectedLayout>} />
      <Route path="/payroll/:runId" element={<Navigate to="/home" replace />} />
      <Route path="/bonuses" element={<Navigate to="/home" replace />} />
      <Route path="/bonuses/runs/:id" element={<Navigate to="/home" replace />} />
      <Route path="/budget" element={<Navigate to="/operations/budget" replace />} />
      <Route path="/hr" element={<ProtectedLayout><HR /></ProtectedLayout>} />
      <Route path="/operations/calendar" element={<Navigate to="/people?view=calendar" replace />} />
      <Route path="/calendar" element={<ProtectedLayout><TotoCalendar /></ProtectedLayout>} />
      <Route path="/documents" element={<ProtectedLayout><DocumentsPage /></ProtectedLayout>} />
      <Route path="/documents/workflow/:id" element={<ProtectedLayout><WorkflowDocumentView /></ProtectedLayout>} />
      <Route path="/documents/:id" element={<ProtectedLayout><DocumentDetail /></ProtectedLayout>} />


      <Route path="/operations/emails" element={<ProtectedLayout><React.Suspense fallback={null}><EmailBuilder /></React.Suspense></ProtectedLayout>} />
      <Route path="/operations/budget" element={<ProtectedLayout><BudgetWorkspace /></ProtectedLayout>} />
      <Route path="/operations" element={<Navigate to="/workspaces" replace />} />
      <Route path="/operations/inventory" element={<ProtectedLayout><InventoryDashboard /></ProtectedLayout>} />
      <Route path="/operations/inventory/audit" element={<ProtectedLayout><RetailAudit /></ProtectedLayout>} />
      <Route path="/operations/inventory/audit/:sessionId" element={<ProtectedLayout><RetailAudit /></ProtectedLayout>} />
      <Route path="/operations/inventory/audit/:sessionId/variances" element={<ProtectedLayout><VarianceReview /></ProtectedLayout>} />
      <Route path="/operations/inventory/audit/:sessionId/:category" element={<ProtectedLayout><RetailAudit /></ProtectedLayout>} />
      <Route path="/operations/inventory/receiving" element={<ProtectedLayout><ReceivingRoute /></ProtectedLayout>} />
      <Route path="/operations/inventory/linen" element={<ProtectedLayout><Linen /></ProtectedLayout>} />
      <Route path="/operations/inventory/receiving/:deliveryId" element={<ProtectedLayout><ReceivingRoute /></ProtectedLayout>} />
      <Route path="/operations/inventory/variances" element={<ProtectedLayout><VariancesLanding /></ProtectedLayout>} />
      <Route path="/labs/presence" element={<ProtectedLayout><PresenceLab /></ProtectedLayout>} />
      <Route path="/reviews/yext" element={<ProtectedLayout><YextImport /></ProtectedLayout>} />
      <Route path="/reviews" element={<ProtectedLayout><GoogleReviewEvidence /></ProtectedLayout>} />
      <Route path="/home/toto" element={<ProtectedLayout><TotoIntroduction /></ProtectedLayout>} />
      <Route path="/settings/program-audit" element={<ProtectedLayout><ProgramAudit /></ProtectedLayout>} />
      <Route path="/settings/backgrounds" element={<ProtectedLayout><BackgroundSettings /></ProtectedLayout>} />
      <Route path="/profile" element={<ProtectedLayout><TotoProfile /></ProtectedLayout>} />
      <Route path="/settings/floor" element={<ProtectedLayout><FloorPlanSettings /></ProtectedLayout>} />
      <Route path="/journey" element={<ProtectedLayout><People key="team-directory" /></ProtectedLayout>} />
      <Route path="/journey/guide" element={<ProtectedLayout><JourneyGuide /></ProtectedLayout>} />
      <Route path="/prototypes/liquid-glass" element={<ProtectedLayout><LiquidGlassPrototype /></ProtectedLayout>} />
      {/* Prototype set — Katrina first-experience review (Feb 2026). Unauthenticated for review. */}
      <Route path="/prototypes" element={researchEnabled ? <PrototypeIndex /> : <Navigate to="/login" replace />} />
      <Route path="/prototypes/welcome-katrina" element={<WelcomeKatrina />} />
      <Route path="/prototypes/katrina-home" element={<KatrinaHome />} />
      <Route path="/prototypes/message-opened" element={<MessageOpened />} />
      <Route path="/prototypes/handoff" element={<Handoff />} />
      <Route path="/prototypes/sonmi-introduction" element={<SonmiIntroduction />} />
      <Route path="/prototypes/payroll-understanding" element={<PayrollUnderstanding />} />
      <Route path="/preview/payroll-engine" element={<PayrollEnginePreview />} />
      <Route path="/prototypes/coo-home" element={<CooHome />} />
      <Route path="/prototypes/presence-reveal" element={<PresencePayrollReveal />} />
      {/* SONMI visual studies (Feb 2026) — hidden, static, unauthenticated. */}
      <Route path="/sonmi/study/shell" element={<SonmiStudyShell />} />
      <Route path="/sonmi/study/people-vessel" element={<SonmiStudyPeopleVessel />} />
      <Route path="/sonmi/study/expanded" element={<SonmiStudyExpanded />} />
      <Route path="/sonmi/study/vessel" element={<SonmiStudyVessel />} />
      <Route path="/sonmi/study/vessel-correction" element={<SonmiStudyVesselCorrection />} />
      <Route path="/sonmi/study/vessel-coauthored" element={<SonmiStudyVesselCoAuthored />} />
      <Route path="/sonmi/first-arrival" element={<FirstArrival />} />
      <Route path="/journey/paths/:id" element={<ProtectedLayout><JourneyPathDetail /></ProtectedLayout>} />
      <Route path="/scheduling" element={<ProtectedLayout><Scheduling /></ProtectedLayout>} />
      {/* Preserve the Operations doorway while Scheduling owns its own vessel. */}
      <Route path="/operations/scheduling" element={<Navigate to="/scheduling" replace />} />
      <Route path="/scheduling/mt" element={<ProtectedLayout><ScheduleMT /></ProtectedLayout>} />
      <Route path="/scheduling/mt/:periodId" element={<ProtectedLayout><ScheduleMT /></ProtectedLayout>} />
      <Route path="/scheduling/eg" element={<ProtectedLayout><ScheduleEG /></ProtectedLayout>} />
      <Route path="/scheduling/eg/:periodId" element={<ProtectedLayout><ScheduleEG /></ProtectedLayout>} />
      {/* Safe, local review surface. It is unauthenticated and uses only sample data. */}
      <Route path="/preview/navigation" element={<React.Suspense fallback={null}><NavigationPreview /></React.Suspense>} />
      <Route path="/preview/eg-scheduling" element={<ScheduleEGPreview />} />
      <Route path="/scheduling/time-off" element={<ProtectedLayout><TimeOff /></ProtectedLayout>} />
      <Route path="/scheduling/preferences" element={<ProtectedLayout><SchedulingPreferences /></ProtectedLayout>} />
      <Route path="/scheduling/zenoti" element={<ProtectedLayout><ZenotiQueue /></ProtectedLayout>} />
      <Route path="/settings/connections/zenoti" element={<Navigate to="/profile?view=connections" replace />} />
      <Route path="/my-schedule" element={<ProtectedLayout><MySchedule /></ProtectedLayout>} />

      <Route path="/scheduling/coverage" element={<ProtectedLayout><Coverage mode="manager" /></ProtectedLayout>} />
      <Route path="/scheduling/coverage/:oppId" element={<ProtectedLayout><Coverage mode="manager" /></ProtectedLayout>} />
      <Route path="/help-a-teammate" element={<ProtectedLayout><Coverage mode="employee" /></ProtectedLayout>} />
      <Route path="/help-a-teammate/:oppId" element={<ProtectedLayout><Coverage mode="employee" /></ProtectedLayout>} />
      <Route path="/scheduling/swaps" element={<ProtectedLayout><Swaps mode="manager" /></ProtectedLayout>} />
      <Route path="/scheduling/swaps/:oppId" element={<ProtectedLayout><Swaps mode="manager" /></ProtectedLayout>} />
      <Route path="/help-a-teammate/swaps" element={<ProtectedLayout><Swaps mode="employee" /></ProtectedLayout>} />
      <Route path="/help-a-teammate/swaps/:oppId" element={<ProtectedLayout><Swaps mode="employee" /></ProtectedLayout>} />
      <Route path="/daily" element={<ProtectedLayout><Daily /></ProtectedLayout>} />
      <Route path="/operations/journey-planning" element={<ProtectedLayout><JourneyPlanning /></ProtectedLayout>} />
      <Route path="/companion" element={<ProtectedLayout><CompanionChat /></ProtectedLayout>} />
      <Route path="/companion/assets" element={<ProtectedLayout><CompanionAssets /></ProtectedLayout>} />
      {/* Backwards-compat aliases — /toto/* silently redirects to /companion/*
          so existing bookmarks and demo scripts keep working. Kept intentionally. */}
      <Route path="/toto" element={<Navigate to="/companion" replace />} />
      <Route path="/toto/assets" element={<Navigate to="/companion/assets" replace />} />
      <Route path="*" element={<ProtectedLayout><Home /></ProtectedLayout>} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <PageContextProvider>
        <CompanionProvider>
          <DeploymentContentProvider>
            <CompanionAssetsProvider>
              <BrowserRouter>
                <Router />
                <Toaster position="top-center" />
              </BrowserRouter>
            </CompanionAssetsProvider>
          </DeploymentContentProvider>
        </CompanionProvider>
      </PageContextProvider>
    </AuthProvider>
  );
}


